// AirQL grammar model + a cursor-aware analyzer for the `/search` composer.
//
// The keyword list mirrors air2-api's QueryKeyword enum: the AirQL token a user
// types is the enum constant name lowercased (e.g. IS_BLOCKLISTED → is_blocklisted,
// column "is_in_blocklist"). Human examples come from cypex's search bar. Each
// keyword's `scope` (ip / domain) drives which explorer endpoint /search hits.
//
// The analyzer is caret-aware (the upgrade over cypex's last-word approach): it
// looks at the fragment immediately left of the caret and decides whether to
// suggest keywords, values, or operators. ponytail: group shorthand `key:(a,b)`
// and quoted/date values are typed by hand — the analyzer guides the 90% case
// (keyword → value → operator); add group-aware value insertion if users ask.

export type Entity = 'ip' | 'domain';

export interface AirqlKeyword {
  key: string; // the AirQL token, e.g. "risk_score"
  example: string; // hint shown in the row / value template
  scope: Entity[]; // entity types that accept this keyword
  values?: string[]; // fixed value choices (bool / enum)
  valuesKey?: string; // filter-values map key holding a dynamic value list
  date?: boolean; // date range → insert a `between '' and ''` template
}

const BOOL = ['true', 'false'];
const IP: Entity[] = ['ip'];
const DOMAIN: Entity[] = ['domain'];
const BOTH: Entity[] = ['ip', 'domain'];

// Example date range for the `between` hint: always [yesterday, today] in the
// parser's `'YYYY-MM-DD'` form (QueryParserTest requires single quotes, no time
// part needed). Local date, so no timezone drift. ponytail: computed at module
// load so it rolls with the calendar — a tab left open past midnight shows a
// day-old example (harmless for a hint); move into analyzeQuery if per-render
// freshness ever matters.
const isoDay = (offset: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const DATE_RANGE_EXAMPLE = `between '${isoDay(-1)}' and '${isoDay(0)}'`;

// Mirrors cypex allowedKeysWithExamples (which mirrors QueryKeyword). `valuesKey`
// points at the filter-values list; `values` is a fixed enum; `date` is a range.
export const AIRQL_KEYWORDS: AirqlKeyword[] = [
  { key: 'port', example: '8080', scope: IP },
  { key: 'risk_score', example: 'high | suspicious | unrated', scope: BOTH, values: ['high', 'suspicious', 'unrated'] },
  { key: 'emerging_threat_24h', example: 'true | false', scope: IP, values: BOOL },
  { key: 'emerging_threat_7d', example: 'true | false', scope: IP, values: BOOL },
  { key: 'emerging_threat_30d', example: 'true | false', scope: IP, values: BOOL },
  { key: 'is_blocklisted', example: 'true | false', scope: IP, values: BOOL },
  { key: 'is_scanner', example: 'true | false', scope: IP, values: BOOL },
  { key: 'scanner', example: 'stretchoid', scope: IP, valuesKey: 'scanners' },
  { key: 'threat_group', example: 'mythic', scope: IP, valuesKey: 'threat_groups' },
  { key: 'pulse_intent', example: 'Temporary Email', scope: IP, valuesKey: 'pulse_intent' },
  { key: 'has_pulse_activity', example: 'true | false', scope: IP, values: BOOL },
  { key: 'protocol', example: 'HTTP', scope: IP },
  { key: 'product', example: 'Apache HTTP Server', scope: IP, valuesKey: 'products' },
  { key: 'vendor', example: 'Cisco', scope: IP, valuesKey: 'vendors' },
  { key: 'technology', example: 'OpenSSH 6.6.1p1', scope: IP },
  { key: 'cidr', example: '10.10.1.0/24', scope: IP },
  { key: 'tag', example: 'dahua_web_version=3.2.7', scope: BOTH, valuesKey: 'tags' },
  { key: 'risk_tag', example: 'c2', scope: IP, valuesKey: 'risk_tags' },
  { key: 'ip_country', example: 'US | USA', scope: IP },
  { key: 'cve', example: 'CVE-2021-1234', scope: IP },
  { key: 'company_name', example: 'Amazon.com, Inc.', scope: IP },
  { key: 'commercial_vpn_company', example: 'ExpressVPN', scope: IP, valuesKey: 'commercial_vpn_companies' },
  { key: 'datacenter_proxy_provider', example: '911Proxy', scope: IP, valuesKey: 'datacenter_proxy_providers' },
  { key: 'residential_proxy_provider', example: 'NetNutProxy', scope: IP, valuesKey: 'residential_proxy_providers' },
  { key: 'has_linked_ips', example: 'true | false', scope: IP, values: BOOL },
  { key: 'is_commercial_vpn', example: 'true | false', scope: IP, values: BOOL },
  { key: 'pulse_volume', example: 'dedicated | shared', scope: IP, values: ['dedicated', 'shared'] },
  { key: 'asn', example: '13335', scope: IP },
  { key: 'isp', example: 'Comcast Cable Communications, LLC', scope: IP },
  { key: 'http_status_code', example: '204', scope: IP },
  { key: 'html_title', example: '"Welcome to nginx!"', scope: BOTH },
  { key: 'is_anonymizer', example: 'true | false', scope: IP, values: BOOL },
  { key: 'is_residential_proxy', example: 'true | false', scope: IP, values: BOOL },
  { key: 'is_mobile', example: 'true | false', scope: IP, values: BOOL },
  { key: 'is_satellite', example: 'true | false', scope: IP, values: BOOL },
  { key: 'is_datacenter_proxy', example: 'true | false', scope: IP, values: BOOL },
  { key: 'is_datacenter', example: 'true | false', scope: IP, values: BOOL },
  { key: 'creation_date', example: DATE_RANGE_EXAMPLE, scope: DOMAIN, date: true },
  { key: 'expiration_date', example: DATE_RANGE_EXAMPLE, scope: DOMAIN, date: true },
  { key: 'update_date', example: DATE_RANGE_EXAMPLE, scope: DOMAIN, date: true },
  { key: 'domain', example: 'example.com', scope: DOMAIN },
  { key: 'registrar', example: 'Cloudflare, Inc.', scope: DOMAIN },
  { key: 'ns_record_host', example: 'ns1.parkingcrew.net', scope: DOMAIN },
  { key: 'a_record_ip', example: '8.8.8.8', scope: DOMAIN },
  { key: 'mx_record_host', example: 'gmail-smtp-in.l.google.com', scope: DOMAIN },
  { key: 'cname_records_alias', example: 'va02.ingress.herokuapp.com', scope: DOMAIN },
  { key: 'is_privacy_enabled', example: 'true | false', scope: DOMAIN, values: BOOL },
  { key: 'dns_provider', example: 'Amazon.com, Inc.', scope: DOMAIN },
  { key: 'hosting_provider', example: 'Google LLC', scope: DOMAIN },
  { key: 'hosting_country', example: 'USA', scope: DOMAIN },
  { key: 'server_header', example: 'nginx', scope: DOMAIN },
  { key: 'cert_fingerprint_sha_1', example: '9cf88995cbe76e15cf3a504eafa824d05fcfbas6', scope: BOTH },
  { key: 'cert_subject_cn', example: 'tls.automattic.com', scope: DOMAIN },
  { key: 'cert_subject_dn', example: '/CN=tls.automattic.com', scope: DOMAIN },
  { key: 'cert_issuer_cn', example: 'Go Daddy Secure Certificate Authority - G2', scope: DOMAIN },
  { key: 'cert_issuer_dn', example: "/C=US/CN=R13/O=Let's Encrypt", scope: DOMAIN },
  { key: 'has_mx_record', example: 'true | false', scope: DOMAIN, values: BOOL },
  // Fixed buckets from air2-api's domain_age column (not a free number) — EQUALS on
  // one of these exact strings. Values hold spaces, so they insert quoted.
  {
    key: 'domain_age',
    example: '0-30 Days | 1-5 Years | 5+ Years',
    scope: DOMAIN,
    values: ['0-30 Days', '31-90 Days', '91-365 Days', '1-5 Years', '5+ Years'],
  },
  { key: 'html_body_sha256', example: '841f25e1d0c14e717674685abbf0bbf83d44e90e', scope: BOTH },
  { key: 'cert_fingerprint_sha256', example: 'cb891e2aeb70488bda6bd25ffefa46b85e8c93b6', scope: BOTH },
];

const BY_KEY: Record<string, AirqlKeyword> = Object.fromEntries(AIRQL_KEYWORDS.map((k) => [k.key, k]));

/**
 * Inside an open value-group `key:(a,b,…)` returns `key`; null otherwise. Walks
 * back to the nearest UNMATCHED `(` and only treats it as a value group when it's
 * preceded by `key:` — a bare `(` (a condition group like `(port:80 OR …)`) stays
 * null so the caller falls back to keyword mode. Mirrors air2-api's GROUP_PATTERN.
 */
function groupKey(value: string, fragStart: number, bodyStart: number): string | null {
  let depth = 0;
  for (let i = fragStart - 1; i >= bodyStart; i--) {
    const ch = value[i];
    if (ch === ')') depth++;
    else if (ch === '(') {
      if (depth === 0) return /([a-z_]+):$/i.exec(value.slice(bodyStart, i))?.[1].toLowerCase() ?? null;
      depth--;
    }
  }
  return null;
}

// Keywords that accept the LIKE operator (`keyword:~value`), mirrored from
// air2-api QueryKeyword.supportsLikeOperator. The server rejects `:~` on any
// other keyword, so the analyzer offers no `:~` suggestions for those — the
// goal is to only ever autocomplete a VALID query.
const LIKE_KEYS = new Set([
  'company_name', 'commercial_vpn_company', 'datacenter_proxy_provider', 'residential_proxy_provider',
  'technology', 'product', 'vendor', 'tag', 'risk_tag', 'cve', 'http_status_code', 'html_title',
  'html_body_sha256', 'threat_group', 'pulse_intent', 'scanner', 'asn', 'isp', 'registrar', 'ns_record_host',
  'mx_record_host', 'a_record_ip', 'cname_records_alias', 'dns_provider', 'hosting_provider',
  'server_header', 'cert_subject_cn', 'cert_subject_dn', 'cert_issuer_cn', 'cert_issuer_dn', 'has_mx_record',
  'domain_age', 'domain',
]);

// The dynamic value lists from GET /ui/filter-values. `tags` is nested
// (parent → values, plus a flat `label` array); the rest are flat string lists.
export type FilterValues = Record<string, string[] | Record<string, string[]>>;

export interface Suggestion {
  label: string; // primary text in the row
  hint?: string; // muted text (example / description)
  insert: string; // spliced in place of [fragStart, caret]
  /** caret position within `insert` after accepting (default: end of insert). */
  caret?: number;
  /** The typed fragment that matched — the menu highlights this run inside `label`. */
  match?: string;
  /** An operator affordance (`:` equals / `:~` contains / `:(…)` any-of), not a real
   *  token — excluded from `complete` so its insert equalling the fragment doesn't
   *  auto-close the menu (the equals row inserts exactly `key:`). */
  operator?: boolean;
}

export interface Analysis {
  fragStart: number; // absolute index in the full value where the fragment begins
  fragEnd: number; // = caret
  items: Suggestion[];
  /** The fragment already IS one of the offered tokens — nothing left to complete.
   *  The menu closes on this so Enter submits instead of re-inserting what's typed. */
  complete: boolean;
}

// The bare words a query may contain besides its conditions: the operators, plus
// `between`, which sits between a keyword and its two quoted dates.
const AIRQL_WORDS = new Set(["and", "or", "not", "between"]);

/**
 * Every keyword air2-api's parser accepts before a colon.
 *
 * `AIRQL_KEYWORDS` drives the SUGGESTION menu, and `ip` is deliberately absent
 * from it — the search bar has always taken a bare address as an indicator, so
 * nobody types `ip:`. The parser accepts it all the same (QueryKeyword.IP), and
 * `ip:(…)` is exactly what a converted paste produces, so recognising a query
 * needs the wider set. Kept separate rather than added to AIRQL_KEYWORDS: that
 * list is what the menu offers, and this is not something to offer.
 */
const QUERY_ONLY_KEYS = ['ip'];
export const AIRQL_QUERY_KEYS: ReadonlySet<string> = new Set([
  ...AIRQL_KEYWORDS.map((k) => k.key),
  ...QUERY_ONLY_KEYS,
]);

/**
 * Is this whole string an AirQL query, as air2-api would decide it?
 *
 * Mirrors QueryParser.isQuerySyntax: take everything before the FIRST colon,
 * drop a leading `(` and a leading `not`, and ask whether what is left is a
 * keyword. That is literally the server's fork — on true it runs the parser, on
 * false it falls back to scraping indicators out of the text — so matching it
 * here is what makes the client's routing agree with the answer it will get.
 *
 * Deliberately not "contains a keyword": `check port:22 hosts` is a question for
 * the agent, and only a string that IS a query may be routed as one.
 */
export function isAirqlQuery(text: string): boolean {
  const t = text.trim();
  const i = t.indexOf(':');
  if (i < 0) return false;
  const head = t
    .slice(0, i)
    .replace('(', '')
    .replace(/^\s*not\s+/i, '')
    .trim()
    .toLowerCase();
  if (!AIRQL_QUERY_KEYS.has(head)) return false;
  // The head alone is what the SERVER checks, and it is not enough here: it makes
  // "domain: which ones are risky?" a query, which then opens a results window
  // showing a parse error instead of reaching the agent. `detectLookup`'s other
  // detectors are all anchored to the whole message for exactly this reason, so be
  // anchored too — every chunk has to be part of a query.
  //
  // Erring strict is the safe direction: a rejected query goes to the agent, which
  // is where an ambiguous sentence belonged anyway.
  return t.split(/\s+/).every((chunk) => {
    if (/^['"]/.test(chunk)) return true; // a quoted value: 'html_title:"…"', a date
    const bare = chunk.replace(/^[("']+/, "").replace(/[)"',]+$/, "");
    if (!bare) return true; // a stray paren the tokenizer handles
    return AIRQL_WORDS.has(bare.toLowerCase()) || bare.includes(":");
  });
}

export const SEARCH_PREFIX = '/search ';

// Values for a keyword+partial. Handles the `tag` two-level shape; everything
// else is a flat filtered list. Returns bare values (no `key:` prefix).
function valueCandidates(kw: AirqlKeyword, partial: string, fv: FilterValues | null): string[] {
  const lower = partial.toLowerCase();
  if (kw.values) return kw.values.filter((v) => v.startsWith(lower));

  if (kw.key === 'tag') {
    const tags = (fv?.tags as Record<string, string[]> | undefined) ?? {};
    if (!partial.includes('=')) {
      const parents = Object.keys(tags)
        .filter((p) => p !== 'label' && p.toLowerCase().includes(lower))
        .map((p) => `${p}=`);
      const labels = (tags.label ?? []).filter((v) => v.toLowerCase().includes(lower));
      return [...parents, ...labels].slice(0, 50);
    }
    const [parent, partialVal = ''] = partial.split('=');
    return (tags[parent] ?? [])
      .filter((v) => v.toLowerCase().includes(partialVal.toLowerCase()))
      .map((v) => `${parent}=${v}`)
      .slice(0, 50);
  }

  if (kw.valuesKey) {
    const list = (fv?.[kw.valuesKey] as string[] | undefined) ?? [];
    return list.filter((v) => v.toLowerCase().includes(lower)).slice(0, 50);
  }
  return [];
}

/**
 * Decide what to suggest at `caret` in `value`. Returns null when the value isn't
 * a `/search …` composer string. Otherwise returns the fragment span to replace
 * and the ranked suggestions (keywords, values, or operators depending on the
 * fragment under the caret).
 */
export function analyzeQuery(value: string, caret: number, fv: FilterValues | null = null): Analysis | null {
  if (!value.startsWith(SEARCH_PREFIX)) return null;
  const bodyStart = SEARCH_PREFIX.length;
  if (caret < bodyStart) return null;

  // Fragment = run of chars after the last separator up to caret. Comma is a
  // separator too so a value inside a `key:(a,b)` group filters on the last item.
  let fragStart = bodyStart;
  for (let i = caret - 1; i >= bodyStart; i--) {
    const ch = value[i];
    if (ch === ' ' || ch === '(' || ch === ')' || ch === ',') {
      fragStart = i + 1;
      break;
    }
  }
  const frag = value.slice(fragStart, caret);
  const done = (items: Suggestion[]): Analysis => ({
    fragStart,
    fragEnd: caret,
    items: items.slice(0, 50),
    complete: items.some((s) => s.insert === frag && !s.operator),
  });

  // Value mode: the fragment already has a `key:`.
  const colon = frag.indexOf(':');
  if (colon !== -1) {
    const key = frag.slice(0, colon).toLowerCase();
    // `keyword:~value` is the LIKE (substring) operator — the separator is `:~`.
    // Strip the leading `~` before filtering, but keep it in the insert so the
    // query stays `keyword:~value`. Server-side only some keywords support like;
    // the analyzer stays permissive (as it is for every value) and lets the
    // backend reject an unsupported one.
    const rawPartial = frag.slice(colon + 1);
    const like = rawPartial.startsWith('~');
    const sep = like ? ':~' : ':';
    const partial = like ? rawPartial.slice(1) : rawPartial;
    const kw = BY_KEY[key];
    if (!kw) return done([]);
    // `:~` on a keyword that doesn't support like → invalid query, offer nothing.
    if (like && !LIKE_KEYS.has(key)) return done([]);
    if (kw.date) {
      const insert = `${key}:between '' and ''`;
      return done([{ label: kw.example, hint: 'date range', insert, caret: `${key}:between '`.length }]);
    }
    const values: Suggestion[] = valueCandidates(kw, partial, fv).map((v) => {
      // A bare value can't hold whitespace or parens — the tokenizer matches
      // `[^\s()]+`, so a multi-word value is SILENTLY TRUNCATED to its first word
      // (verified against air2-api QueryParserTest). Wrap those in double quotes;
      // the parser strips them. A `=`-tag parent (level 1) never has spaces and
      // must stay unquoted + space-less so the level-2 drill-down keeps working.
      const q = /[\s()]/.test(v) ? `"${v}"` : v;
      // No trailing space: the caret stops at the end of the finished token so Enter
      // submits. SPACE is the user's explicit "give me the next options" gesture — it
      // reopens the menu on AND/OR. A `=` parent (level 1) likewise stays put so the
      // next analysis drills into its level-2 values (`tag:parent=value`). ponytail:
      // shape-based, not `key === 'tag'`, so any two-level keyword is covered.
      return { label: v, insert: `${key}${sep}${v.endsWith('=') ? v : q}`, match: partial };
    });
    // Discoverable group shorthand: right after `keyword:` (empty value) on a
    // keyword backed by a value SET, offer `keyword:(…)`. air2-api expands
    // `keyword:(a,b)` to an OR over the values (works on any keyword); the row
    // inserts `keyword:(` with the caret inside so the next keystroke filters the
    // first group item. Gated to keywords with a real pick-list — a dynamic list
    // (`valuesKey`) or a non-bool enum (`values`, e.g. risk_score / domain_age).
    // Skipped for bool (`(true,false)` matches everything), free-text (no list to
    // show → empty dropdown), and `tag` (its `parent=value` drilldown doesn't fit
    // the flat comma group). Unshifted before the `:~` row so `:~` stays on top.
    const hasValueSet = kw.valuesKey != null || (kw.values != null && kw.values !== BOOL);
    if (!like && partial === '' && hasValueSet && key !== 'tag') {
      values.unshift({ label: `${key}:(…)`, hint: 'any of', insert: `${key}:(`, operator: true });
    }
    // Make the LIKE operator discoverable: right after `keyword:` (empty value, and
    // not already in `:~`), offer a row that switches to substring match. Inserts
    // `keyword:~` with no trailing space, so the next keystroke filters as `like`.
    if (!like && partial === '' && LIKE_KEYS.has(key)) {
      values.unshift({ label: `${key}:~`, hint: 'contains', insert: `${key}:~`, operator: true });
    }
    // Equals is the base operator (server splits on `:` — QueryParser). Offer it too,
    // right after `keyword:`, for parity with contains / any-of (VIS-425). Unshifted
    // LAST so it sits on top. Its insert equals the fragment, so `operator: true`
    // keeps the menu open (see the `complete` check).
    if (!like && partial === '') {
      values.unshift({ label: `${key}:`, hint: 'equals', insert: `${key}:`, operator: true });
    }
    return done(values);
  }

  // Group value mode: inside an open `key:(a,b,…)` each comma item is a value for
  // `key`. Suggest the same value list; insert the value with no `key:` prefix and
  // no trailing space — the user types the next `,` or the closing `)`. Every group
  // value is quoted UNIFORMLY (even single words) so a `(Nmap,"Speed Test")` mix
  // reads consistently; air2-api's tokenizer strips the quotes per item on expand
  // (QueryTokenizer.split → replaceAll), so a quoted single word is equivalent.
  const gkey = groupKey(value, fragStart, bodyStart);
  if (gkey) {
    const kw = BY_KEY[gkey];
    if (!kw) return done([]);
    // Prime the next pick after the FIRST group value with a trailing comma (the
    // opening `(` sits right before the fragment). Later values (fragment follows a
    // `,`) don't get one — the user closes the group or types their own comma, so
    // we never leave a dangling `,)` that would expand to an empty condition.
    const tail = value[fragStart - 1] === '(' ? ',' : '';
    // Drop values already picked in this group (everything between the opening `(`
    // and the current fragment, comma-split, quotes stripped) so they don't repeat.
    const open = value.lastIndexOf('(', fragStart - 1);
    const picked = new Set(
      value
        .slice(open + 1, fragStart)
        .split(',')
        .map((s) => s.trim().replace(/^["']|["']$/g, ''))
        .filter(Boolean),
    );
    return done(
      valueCandidates(kw, frag, fv)
        .filter((v) => !picked.has(v))
        .map((v) => ({ label: v, insert: `"${v}"${tail}`, match: frag })),
    );
  }

  // Keyword / operator mode. After a COMPLETED condition (a `key:value`, a closed
  // quoted value, or a closed `)` group) only a binary operator may follow. At a
  // condition START (query start, after AND/OR, after `(`) a keyword, the `NOT`
  // prefix, or a `(` opener may follow. Grammar: `a NOT b` and `a (…)` without a
  // binary operator between them are parse errors (air2-api QueryParser), so the
  // two sets must never bleed into each other.
  const before = value.slice(bodyStart, fragStart).trim();
  const lastToken = before.split(/[\s()]+/).filter(Boolean).pop() ?? '';
  // A dangling `key:` / `key:~` / `parent=` still owes a VALUE — the grammar has no
  // room for a keyword or an operator there. Offering one is what produced garbage
  // like `risk_score: risk_score:`, so suggest nothing until the value is typed.
  if (/^[a-z_]+:~?$/i.test(lastToken) || lastToken.endsWith('=')) return done([]);
  const expectOperator = /^[a-z_]+:.+/i.test(lastToken) || /["')]$/.test(before);
  const fl = frag.toLowerCase();

  if (expectOperator) {
    // A just-closed `)` is a FINISHED condition and the caret is still glued to it —
    // the user typed no separator, so nothing was asked for. Offering AND/OR there
    // popped a menu over a query that was ready to run, and Enter then inserted the
    // operator instead of submitting, gluing it on as `…,ftp)AND`. SPACE is the
    // explicit "what comes next" gesture (the same contract `accept` relies on when
    // it closes the menu on a finished token), so stay quiet until it arrives.
    if (frag === '' && !/\s/.test(value[caret - 1] ?? '')) return done([]);
    return done(['AND', 'OR'].filter((o) => o.toLowerCase().startsWith(fl)).map((o) => ({ label: o, insert: o })));
  }

  // Condition start: the NOT prefix and `(` group opener, then the keywords. Ops
  // go FIRST so the 50-row cap can't slice them off behind the keyword list.
  // Substring match (not just prefix): typing `thre` also surfaces
  // `emerging_threat_*` / `threat_group`. Prefix hits still rank first (stable
  // sort), so `por` keeps `port` on top. The matched run is highlighted in the menu.
  const keywordItems: Suggestion[] = AIRQL_KEYWORDS.filter((k) => k.key.includes(fl))
    .sort((a, b) => Number(b.key.startsWith(fl)) - Number(a.key.startsWith(fl)))
    .map((k) => ({
      label: k.key,
      hint: k.example,
      insert: `${k.key}:`,
      match: fl,
    }));
  // Right after a NOT, a CONDITION must follow — the tokenizer rejects `NOT (`,
  // `NOT NOT` and `NOT AND/OR`. So offer only keywords there, no starter ops.
  const postNot = lastToken.toUpperCase() === 'NOT';
  const starterOps: Suggestion[] = postNot
    ? []
    : ['NOT', '(']
        .filter((o) => o.toLowerCase().startsWith(fl))
        .map((o) => ({ label: o, insert: o }));
  return done([...starterOps, ...keywordItems]);
}

/**
 * Like {@link analyzeQuery} but for a bare AirQL body with no `/search ` prefix —
 * used by standalone search bars (e.g. the explorer tab strip). `caret` is an
 * index into `query`; the returned fragment span is shifted back into `query`.
 */
export function analyzeBareQuery(query: string, caret: number, fv: FilterValues | null = null): Analysis | null {
  const a = analyzeQuery(SEARCH_PREFIX + query, caret + SEARCH_PREFIX.length, fv);
  if (!a) return null;
  return { ...a, fragStart: a.fragStart - SEARCH_PREFIX.length, fragEnd: a.fragEnd - SEARCH_PREFIX.length };
}
